import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogContent,
    Tabs,
    Tab,
    Divider,
    Link,
    Alert,
    CircularProgress,
    Paper,
    TextField,
    Chip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch
} from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { useTeam } from 'team/contexts/teamContext';
import {
    getIntegrations,
    getTeamIntegrations,
    connectTeamIntegration,
    updateTeamIntegrationSettings,
    syncTeamIntegration,
    getTeamIntegrationGroups
} from 'team/apis/teamIntegrationApi';

const TeamSyncMicrosoftAdContent = ({ onDataChange, onValidationChange }) => {
    const { currentTeamId } = useTeam();
    const [setupOpen, setSetupOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [integration, setIntegration] = useState(null);
    const [connection, setConnection] = useState(null);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupSearch, setGroupSearch] = useState('');
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [config, setConfig] = useState({
        groups: [],
        autoSyncCadence: 'daily',
        deleteMembersOnRemoval: false,
        sendEmailInvites: true,
        useAdminRules: false,
        fields: {
            name: { sync: true, overwrite: true, azureField: 'displayName' },
            email: { sync: true, overwrite: true, azureField: 'mailOrUserPrincipalName' },
            jobTitle: { sync: true, overwrite: true, azureField: 'jobTitle' },
            company: { sync: true, overwrite: true, azureField: 'companyName' },
            phoneNumber: { sync: true, overwrite: true, azureField: 'mobileOrBusinessPhone' },
            location: { sync: true, overwrite: true, azureField: 'addressComposite' },
            profilePicture: { sync: true, overwrite: true, azureField: 'profilePhoto' }
        }
    });

    const filteredGroups = useMemo(() => {
        if (!groupSearch.trim()) return availableGroups;
        return availableGroups.filter((group) =>
            group.name.toLowerCase().includes(groupSearch.toLowerCase())
        );
    }, [groupSearch, availableGroups]);

    const lastSyncLabel = useMemo(() => {
        const rawDate = connection?.lastSyncAt;
        if (!rawDate) return 'Not synced yet';
        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) return 'Not synced yet';
        return parsed.toLocaleString();
    }, [connection]);

    const nextSyncLabel = useMemo(() => {
        const cadence = config?.autoSyncCadence || 'daily';
        const cadenceToMs = {
            daily: 24 * 60 * 60 * 1000,
            weekly: 7 * 24 * 60 * 60 * 1000,
            monthly: 30 * 24 * 60 * 60 * 1000,
            quarterly: 90 * 24 * 60 * 60 * 1000
        };
        const baseMs = connection?.lastSyncAt
            ? new Date(connection.lastSyncAt).getTime()
            : Date.now();
        if (Number.isNaN(baseMs)) return 'After first successful sync';
        return new Date(baseMs + (cadenceToMs[cadence] || cadenceToMs.daily)).toLocaleString();
    }, [connection, config]);

    const handleOpenSetup = () => {
        setSetupOpen(true);
        onDataChange(null);
        onValidationChange(false);
    };

    useEffect(() => {
        const fetchIntegrationState = async () => {
            if (!setupOpen || !currentTeamId) return;

            try {
                setLoading(true);
                const [availableResponse, connectedResponse] = await Promise.all([
                    getIntegrations(),
                    getTeamIntegrations(currentTeamId)
                ]);

                const available = availableResponse?.integrations || [];
                const connected = connectedResponse?.integrations || [];

                const adIntegration = available.find((item) =>
                    item?.name?.toLowerCase() === 'active directory'
                );

                setIntegration(adIntegration || null);

                if (adIntegration) {
                    const matchedConnection = connected.find((item) => {
                        const connectedId = item?.integrationId?._id || item?.integrationId;
                        return String(connectedId) === String(adIntegration._id);
                    });

                    setConnection(matchedConnection || null);
                    setIsConnected(matchedConnection?.status === 'connected');

                    const savedConfig = matchedConnection?.settings?.adSyncConfig;
                    if (savedConfig) {
                        setConfig((prev) => ({
                            ...prev,
                            ...savedConfig
                        }));
                    }

                    if (matchedConnection?.status === 'connected') {
                        const groupsResponse = await getTeamIntegrationGroups(currentTeamId, adIntegration._id, '');
                        setAvailableGroups(groupsResponse?.groups || []);
                    }
                }
            } catch (error) {
                setNotification({
                    open: true,
                    message: error.response?.data?.message || 'Failed to load Azure integration status',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchIntegrationState();
    }, [setupOpen, currentTeamId]);

    useEffect(() => {
        const fetchGroupsBySearch = async () => {
            if (!setupOpen || !isConnected || !integration || !currentTeamId) return;
            try {
                setGroupsLoading(true);
                const groupsResponse = await getTeamIntegrationGroups(currentTeamId, integration._id, groupSearch.trim());
                setAvailableGroups(groupsResponse?.groups || []);
            } catch (error) {
                setNotification({
                    open: true,
                    message: error.response?.data?.message || 'Failed to fetch Azure AD groups',
                    severity: 'error'
                });
            } finally {
                setGroupsLoading(false);
            }
        };

        const timeout = setTimeout(fetchGroupsBySearch, 250);
        return () => clearTimeout(timeout);
    }, [groupSearch, setupOpen, isConnected, integration, currentTeamId]);

    const handleConnect = async () => {
        if (!currentTeamId || !integration) return;
        try {
            setConnecting(true);
            const response = await connectTeamIntegration(currentTeamId, integration._id, {});
            if (response?.authUrl) {
                window.location.href = response.authUrl;
                return;
            }

            setIsConnected(true);
            const groupsResponse = await getTeamIntegrationGroups(currentTeamId, integration._id, '');
            setAvailableGroups(groupsResponse?.groups || []);
        } catch (error) {
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to start Microsoft connection',
                severity: 'error'
            });
        } finally {
            setConnecting(false);
        }
    };

    const normalizeSelectedGroup = (group) => {
        if (!group) return null;
        if (typeof group === 'string') return { id: group, name: group };
        if (group.id && group.name) return { id: group.id, name: group.name };
        if (group._id && group.name) return { id: group._id, name: group.name };
        return null;
    };

    const toggleGroup = (group) => {
        const normalized = normalizeSelectedGroup(group);
        if (!normalized) return;

        setConfig((prev) => {
            const normalizedGroups = (prev.groups || []).map((item) => normalizeSelectedGroup(item)).filter(Boolean);
            const exists = normalizedGroups.some((item) => item.id === normalized.id);
            return {
                ...prev,
                groups: exists
                    ? normalizedGroups.filter((item) => item.id !== normalized.id)
                    : [...normalizedGroups, normalized]
            };
        });
    };

    const updateFieldConfig = (fieldKey, key, value) => {
        setConfig((prev) => ({
            ...prev,
            fields: {
                ...prev.fields,
                [fieldKey]: {
                    ...prev.fields[fieldKey],
                    [key]: value
                }
            }
        }));
    };

    const handleSaveConfiguration = async () => {
        if (!currentTeamId || !integration || !isConnected) {
            setNotification({
                open: true,
                message: 'Connect Microsoft first before saving configuration.',
                severity: 'warning'
            });
            return;
        }

        try {
            setSaving(true);
            const response = await updateTeamIntegrationSettings(currentTeamId, integration._id, {
                adSyncConfig: config
            });

            if (response?.connection) {
                setConnection(response.connection);
            } else {
                setConnection((prev) => (
                    prev
                        ? {
                            ...prev,
                            settings: {
                                ...(prev.settings || {}),
                                adSyncConfig: config
                            }
                        }
                        : prev
                ));
            }
            setNotification({
                open: true,
                message: 'Configuration saved successfully.',
                severity: 'success'
            });
        } catch (error) {
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to save configuration',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSyncNow = async () => {
        if (!currentTeamId || !integration || !isConnected) {
            setNotification({
                open: true,
                message: 'Connect Microsoft first before running sync.',
                severity: 'warning'
            });
            return;
        }

        try {
            setSyncing(true);
            const response = await syncTeamIntegration(currentTeamId, integration._id);
            setConnection((prev) => (
                prev
                    ? {
                        ...prev,
                        lastSyncAt: new Date().toISOString(),
                        lastSyncStatus: response?.summary?.failed > 0 ? 'error' : 'success',
                        lastSyncError: response?.summary?.failed > 0
                            ? `${response.summary.failed} member(s) failed during sync`
                            : null
                    }
                    : prev
            ));
            setNotification({
                open: true,
                message: 'Manual sync started. We will process updates shortly.',
                severity: 'success'
            });
        } catch (error) {
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to start sync',
                severity: 'error'
            });
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Box sx={{ padding: '2rem', paddingTop: '3rem', maxWidth: '700px' }}>
            <Typography variant="body2" sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '2rem'
            }}>
                Sync with Active Directory
            </Typography>

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                backgroundColor: '#fff',
                borderRadius: '0.5rem',
                border: '1px solid #e0e0e0',
                width: '50%'
            }}>
                <Typography variant="h6" sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#333',
                    textAlign: 'center'
                }}>
                    Active Directory
                </Typography>
                <Typography variant="body2" sx={{
                    fontSize: '0.875rem',
                    color: '#666',
                    marginTop: '0.5rem',
                    textAlign: 'center',
                    lineHeight: 1.4
                }}>
                    Full platform of marketing, sales, customer service, and CRM software.
                </Typography>

                <Box sx={{
                    display: 'flex',
                    gap: '1rem',
                    marginTop: '1.25rem',
                    justifyContent: 'center'
                }}>
                    <TeamWhiteButtonWithIcon
                        text="Configure"
                        onClick={handleOpenSetup}
                        padding="0.5rem 1.5rem"
                        sx={{ width: '8rem', height: '2.5rem', fontSize: '0.875rem' }}
                    />
                    <TeamDarkButtonWithIcon
                        text="Start Setup"
                        onClick={handleOpenSetup}
                        sx={{ width: '8rem', height: '2.5rem', fontSize: '0.875rem' }}
                    />
                </Box>
            </Box>

            <Dialog
                open={setupOpen}
                onClose={() => setSetupOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: '0.75rem',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #6264A7 0%, #5B5FC7 100%)',
                    color: '#fff',
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                            Microsoft Teams
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', opacity: 0.95 }}>
                            Sync Members from Azure Active Directory
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Button
                            onClick={handleConnect}
                            disabled={connecting || !integration}
                            sx={{
                                backgroundColor: '#fff',
                                color: '#4f4f4f',
                                borderRadius: '999px',
                                textTransform: 'none',
                                fontWeight: 600,
                                padding: '0.35rem 1rem',
                                '&:hover': { backgroundColor: '#f5f5f5' }
                            }}
                        >
                            {connecting ? 'Connecting...' : (isConnected ? 'Connected' : 'Connect')}
                        </Button>
                        <Button
                            onClick={() => setSetupOpen(false)}
                            sx={{ minWidth: '2rem', color: '#fff', padding: '0.25rem' }}
                        >
                            <CloseIcon />
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ backgroundColor: '#fff', borderBottom: '1px solid #ececec' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                        sx={{ paddingX: '1rem' }}
                    >
                        <Tab value="overview" label="Overview" sx={{ textTransform: 'none', fontWeight: 600 }} />
                        <Tab value="configuration" label="Configuration" sx={{ textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>
                </Box>

                <DialogContent sx={{ padding: '1.5rem' }}>
                    {loading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <CircularProgress size={18} />
                            <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>Loading integration settings...</Typography>
                        </Box>
                    )}

                    {!integration && !loading && (
                        <Alert severity="warning" sx={{ marginBottom: '1rem' }}>
                            Active Directory integration was not found in your backend integrations list. Run integration seeding and configure Azure credentials in backend environment variables.
                        </Alert>
                    )}

                    {activeTab === 'overview' && (
                        <Box>
                            <Typography sx={{ color: '#4f4f4f', fontSize: '0.92rem', lineHeight: 1.8 }}>
                                Connect to your Azure Active Directory to sync members from Active Directory to Touch Teams for instant member creation, removal and updates.
                            </Typography>

                            <Box sx={{ marginTop: '1rem' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333' }}>
                                    Step by Step Setup Instructions:
                                </Typography>
                                <Link
                                    href="https://docs.intouchwe.co/introduction/integrations/azure-active-directory"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontSize: '0.9rem', wordBreak: 'break-all' }}
                                >
                                    https://docs.intouchwe.co/introduction/integrations/azure-active-directory
                                </Link>
                            </Box>

                            <Box sx={{ marginTop: '1.2rem' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}>
                                    What&apos;s Included:
                                </Typography>
                                {[
                                    'Sync members from any group or department in Azure Active Directory',
                                    'Sync at any time with a single button or set up auto sync',
                                    'Set up auto sync cadences ranging from daily, weekly, monthly, or quarterly',
                                    'Auto provisioning and de-provisioning of members',
                                    'All synced members will automatically be added to a subteam with the same name as their synced AD group.'
                                ].map((item) => (
                                    <Typography key={item} sx={{ color: '#4f4f4f', fontSize: '0.9rem', lineHeight: 1.8 }}>
                                        - {item}
                                    </Typography>
                                ))}
                            </Box>

                            <Box sx={{ marginTop: '1.2rem' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}>
                                    Your initial sync will create digital cards with the following info:
                                </Typography>
                                {['Name', 'Email', 'Job Title', 'Company', 'Profile photo', 'Phone number', 'Location'].map((item) => (
                                    <Typography key={item} sx={{ color: '#4f4f4f', fontSize: '0.9rem', lineHeight: 1.8 }}>
                                        - {item}
                                    </Typography>
                                ))}
                            </Box>

                            <Divider sx={{ marginY: '1.25rem' }} />
                            <Typography sx={{ color: '#4f4f4f', fontSize: '0.9rem', lineHeight: 1.7 }}>
                                <strong>Questions/Concerns:</strong><br />
                                Please shoot us an email: teams@intouchwe.co or use the chat bubble in the bottom right of your screen.
                            </Typography>
                        </Box>
                    )}

                    {activeTab === 'configuration' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                                <Typography sx={{ fontWeight: 700, marginBottom: '0.5rem' }}>Sync Status</Typography>
                                <Typography sx={{ fontSize: '0.88rem', color: '#4f4f4f' }}>
                                    Last Sync: {lastSyncLabel}
                                </Typography>
                                <Typography sx={{ fontSize: '0.88rem', color: '#4f4f4f' }}>
                                    Next Auto Sync: {nextSyncLabel}
                                </Typography>
                                {connection?.lastSyncStatus === 'error' && connection?.lastSyncError && (
                                    <Typography sx={{ fontSize: '0.82rem', color: '#c62828', marginTop: '0.4rem' }}>
                                        Last Sync Error: {connection.lastSyncError}
                                    </Typography>
                                )}
                            </Paper>

                            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                                <Typography sx={{ fontWeight: 700, marginBottom: '0.5rem' }}>Choosing Groups to Sync</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search Active Directory groups"
                                    value={groupSearch}
                                    onChange={(event) => setGroupSearch(event.target.value)}
                                    sx={{ marginBottom: '0.75rem' }}
                                />
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {filteredGroups.map((group) => {
                                        const selected = (config.groups || []).some((item) => {
                                            const normalized = normalizeSelectedGroup(item);
                                            return normalized?.id === group.id;
                                        });
                                        return (
                                            <Chip
                                                key={group.id}
                                                label={group.name}
                                                clickable
                                                onClick={() => toggleGroup(group)}
                                                color={selected ? 'primary' : 'default'}
                                                variant={selected ? 'filled' : 'outlined'}
                                                sx={{ marginBottom: '0.25rem' }}
                                            />
                                        );
                                    })}
                                    {groupsLoading && (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                                            Loading groups...
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                                <Typography sx={{ fontWeight: 700, marginBottom: '0.5rem' }}>Auto Sync Frequency</Typography>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="ad-cadence-label">Cadence</InputLabel>
                                    <Select
                                        labelId="ad-cadence-label"
                                        label="Cadence"
                                        value={config.autoSyncCadence}
                                        onChange={(event) => setConfig((prev) => ({ ...prev, autoSyncCadence: event.target.value }))}
                                    >
                                        <MenuItem value="daily">Daily (Recommended)</MenuItem>
                                        <MenuItem value="weekly">Weekly</MenuItem>
                                        <MenuItem value="monthly">Monthly</MenuItem>
                                        <MenuItem value="quarterly">Quarterly</MenuItem>
                                    </Select>
                                </FormControl>
                            </Paper>

                            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                                <Typography sx={{ fontWeight: 700, marginBottom: '0.75rem' }}>Select Fields To Sync</Typography>
                                <Stack gap={0.75}>
                                    {Object.entries(config.fields).map(([fieldKey, fieldConfig]) => (
                                        <Box
                                            key={fieldKey}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto auto',
                                                alignItems: 'center',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '0.5rem',
                                                padding: '0.6rem 0.75rem'
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '0.88rem', textTransform: 'capitalize' }}>
                                                {fieldKey === 'profilePicture' ? 'Profile Picture' : fieldKey}
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={fieldConfig.sync}
                                                        onChange={(event) => updateFieldConfig(fieldKey, 'sync', event.target.checked)}
                                                    />
                                                }
                                                label="Sync"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={fieldConfig.overwrite}
                                                        onChange={(event) => updateFieldConfig(fieldKey, 'overwrite', event.target.checked)}
                                                    />
                                                }
                                                label="Overwrite"
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                                <Typography sx={{ fontWeight: 700, marginBottom: '0.5rem' }}>Sync Behavior</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.deleteMembersOnRemoval}
                                            onChange={(event) => setConfig((prev) => ({ ...prev, deleteMembersOnRemoval: event.target.checked }))}
                                        />
                                    }
                                    label="Delete members upon removal from synced AD groups"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.sendEmailInvites}
                                            onChange={(event) => setConfig((prev) => ({ ...prev, sendEmailInvites: event.target.checked }))}
                                        />
                                    }
                                    label="Send email invites to newly synced members"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.useAdminRules}
                                            onChange={(event) => setConfig((prev) => ({ ...prev, useAdminRules: event.target.checked }))}
                                        />
                                    }
                                    label="Use admin rules from Azure roles"
                                />
                            </Paper>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                                <TeamWhiteButtonWithIcon
                                    text={saving ? 'Saving...' : 'Save Configuration'}
                                    onClick={handleSaveConfiguration}
                                    disabled={saving || syncing || !integration}
                                    sx={{ fontSize: '0.8rem' }}
                                />
                                <TeamDarkButtonWithIcon
                                    text={syncing ? 'Syncing...' : 'Sync Now'}
                                    onClick={handleSyncNow}
                                    disabled={saving || syncing || !integration}
                                    sx={{ fontSize: '0.8rem' }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={() => setNotification((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
};

export default TeamSyncMicrosoftAdContent;
